<template>
  <div
    :class="{
      'device-teaser': true,
      'device-teaser--not-reachable': !device.isReachable,
    }"
  >
    <div class="device-teaser__left">
      <img :src="imageUrl" class="device-teaser__img" />
    </div>
    <div class="device-teaser__center">
      <h2 class="device-teaser__title">
        {{ device.name }}
      </h2>
      <div v-if="device.description" class="device-teaser__description">
        {{ device.description }}
      </div>
    </div>
    <div class="device-teaser__right">
      <div
        v-if="device.isReachable === 'unknown'"
        class="device-teaser__spinner"
      >
        <Spinner />
      </div>
      <div
        v-else-if="device.meter === 'true' && device.isReachable === true"
        class="device-teaser__spinner"
      >
        <Meter />
      </div>
      <div
        v-else-if="showClockWhenTimerIsActive && timerIsActive"
        class="device-teaser__timer-icon"
        :title="t('devices.timerActiveCantSwitch')"
      >
        <Clock />
      </div>
      <Toggle
        v-else-if="showDeviceSwitchToggle"
        v-model="deviceIsOnModel"
        :color="colorPrimary"
        :disabled="deviceOnSwitchDisabled"
        :title="device.isOn ? t('devices.turnOff') : t('devices.turnOn')"
        @change="onToggleDeviceOnSwitch"
        @click.prevent=""
      />
    </div>
    <div
      v-if="
        device.isReachable === true &&
        device.meter !== 'true' &&
        !isNaN(device.temperature)
      "
      :class="{
        'device-teaser__current-temperature': true,
        'device-teaser__current-temperature--right': consumption === '',
      }"
      :title="
        t('devices.currentTemperature', {
          '@value': temperature,
        })
      "
    >
      {{ temperature }}
    </div>
    <div
      v-if="
        device.isReachable === true &&
        device.meter !== 'true' &&
        !isNaN(device.wattage)
      "
      class="device-teaser__current-consumption"
      :title="
        t('devices.currentConsumption', {
          '@value': consumption,
        })
      "
    >
      {{ consumption }}
    </div>
  </div>
</template>

<script setup>
  import Spinner from '@/components/icons/Spinner.vue'
  import Clock from '@/components/icons/Clock.vue'
  import Meter from '@/components/icons/Meter.vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { useFormsStore } from '@/stores/useFormsStore'
  import { useDeviceStore } from '@/stores/useDeviceStore'
  import { computed, ref } from 'vue'
  import { assetPath } from '@/util'
  import { useUnits } from '@/composables/useUnits.ts'
  import { useDeviceTimer } from '@/composables/useDeviceTimer.ts'
  import Toggle from '@vueform/toggle'
  import { useColors } from '@/composables/useColors.ts'

  const deviceStore = useDeviceStore()
  const formsStore = useFormsStore()
  const { t } = useStrings()
  const { formatWattage, formatTemperature } = useUnits()
  const { colorPrimary } = useColors()

  const props = defineProps({
    device: {
      type: Object,
        required: true,
    },
    showClockWhenTimerIsActive: {
      type: Boolean,
      default: false,
    },
  })

  const {
    timerIsActive,
  } = useDeviceTimer(props.device)

  const deviceIsOnModel = computed({
    get: () => {
      return props.device.isOn
    },
    set: (value) => {
      props.device.isOn = value
    },
  })

  const onToggleDeviceOnSwitch = () => {
    props.device.isOn = !props.device.isOn
    deviceStore.toggleDeviceOn(props.device)
  }

  const deviceOnSwitchDisabled = computed(() => {
    return formsStore.isFormSubmitting('deviceOn_' + props.device.id)
  })

  const consumption = computed(() => {
    // Only show 0W when needed to get rid of visual clutter.
    if (props.device.isOn || props.device.timer.active) {
      return formatWattage(props.device.wattage)
    }
    return ''
  })

  const showDeviceSwitchToggle = computed(() => {
    // TODO Clean this up.
    return props.device.isReachable === true &&
      (props.device.inverter_type === 'false' || props.device.inverter_type === undefined)
  })

  const imageUrl = assetPath(`device-types/${props.device.deviceType.toLowerCase()}.png`)

  const temperature = computed(() => formatTemperature(props.device.temperature))
</script>

<style lang="scss">
  .device-teaser {
    transition: $transition-base;
    padding: $spacer10 $spacer10 $spacer10 0;
    margin-bottom: $spacer025;
    display: grid;
    $switch-size: 60px;
    grid-template-columns: 70px 1fr $switch-size $switch-size;
    width: 100%;

    // Allow showing the current temperature and the on/off toggle on screens
    // that are a little smaller than $bp-xs, which is some iPhones.
    $show-temp-and-toggle-breakpoint: $bp-xs - 40px;

    @media (max-width: ($show-temp-and-toggle-breakpoint - 1px)) {
      grid-auto-rows: 1fr;
    }

    @media (min-width: $show-temp-and-toggle-breakpoint) {
      grid-template-columns: 85px 1fr $switch-size $switch-size;
    }

    &--not-reachable {
      .device-teaser__img {
        opacity: 0.4;
      }
    }

    &__left {
      margin-right: $spacer10;

      @media (min-width: $show-temp-and-toggle-breakpoint) {
        grid-row-start: 1;
        grid-row-end: 3;
      }
    }

    &__center {
      display: flex;
      align-items: center;
      flex-wrap: wrap;

      @media (min-width: $show-temp-and-toggle-breakpoint) {
        align-items: flex-start;
      }
    }

    &__right {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-left: $spacer05;
      flex-grow: 0;
      flex-shrink: 0;
      grid-column-start: 4;
      grid-column-end: 5;

      @media (min-width: $show-temp-and-toggle-breakpoint) {
        align-items: flex-start;
      }

      @media (min-width: $bp-ssm) {
        margin-left: $spacer10;
      }
    }

    &__current-consumption {
      display: none;
      grid-area: 2 / 3 / 4 / 4;
      margin-left: $spacer05;
      margin-top: $spacer05;
      font-weight: 900;
      grid-column-start: 4;
      grid-column-end: 5;

      @media (min-width: $show-temp-and-toggle-breakpoint) {
        display: flex;
        justify-content: flex-end;
      }
    }

    &__current-temperature {
      display: none;
      margin-top: $spacer05;
      grid-area: 2 / 2 / 4 / 3;
      justify-content: flex-end;
      grid-column-start: 3;
      grid-column-end: 4;

      &--right {
        grid-column-start: 4;
        grid-column-end: 5;
      }

      @media (min-width: $show-temp-and-toggle-breakpoint) {
        display: flex;
      }
    }

    &__img {
      max-width: 100%;
    }

    &__title {
      margin: 0;
      line-height: $line-height-min;
      font-size: $font-size-md;
      hyphens: auto;

      @media (min-width: $show-temp-and-toggle-breakpoint) {
        font-size: $font-size-md;
        line-height: $line-height-sm;
      }

      @media (min-width: $bp-md) {
        font-size: $font-size-lg;
      }
    }

    &__description {
      display: none;
      flex-basis: 100%;
      hyphens: auto;

      @media (min-width: $bp-ssm) {
        display: block;
      }
    }

    &__spinner {
      width: $spacer20;

      .icon {
        animation: spin 1.8s linear 0s infinite;
      }
    }

    &__timer-icon {
      width: $spacer20;
      display: flex;

      @media (min-width: $bp-sm) {
        margin-right: $spacer075;
      }
    }
  }
</style>
